/**
 * A PSD stores a text layer's baseline, not its box top, so a re-render needs the exact ascent and
 * descent the browser used. Those are per-face (Avenir Black 1.0/0.366em, Inter 0.969/0.242) and an
 * assumed ascent lands several pixels off at headline sizes.
 */

/** Distance above/below the baseline, in em. Both positive. */
export type PsdFontMetrics = { ascent: number; descent: number };

/** Metrics for one CSS font-family/weight/slant, as the browser resolves it. */
export type PsdFontMetricsResolver = (args: {
  fontFamily: string;
  fontWeight: string;
  italic: boolean;
}) => PsdFontMetrics;

/** Fallback. Sums to 1em, collapsing baseline math to `ascent × fontSize`. */
export const FALLBACK_FONT_METRICS: PsdFontMetrics = {
  ascent: 0.8,
  descent: 0.2,
};

/** Large enough that reported pixels keep ~3 decimals of em. */
const MEASUREMENT_FONT_SIZE = 1000;

/** Assigning an unparseable font shorthand is a no-op; setting this first makes that detectable. */
const SENTINEL_FONT = "10px monospace";

/** Ascender, descender and x-height, so the box spans the full face. */
const MEASUREMENT_SAMPLE = "Hxg";

/** Measures against `document.fonts`, so it must be used while the ad's faces are loaded. */
export function buildCanvasFontMetricsResolver(): PsdFontMetricsResolver {
  const context = measurementContext();
  if (!context) return () => FALLBACK_FONT_METRICS;

  const cache = new Map<string, PsdFontMetrics>();
  return ({ fontFamily, fontWeight, italic }) => {
    const shorthand = `${italic ? "italic " : ""}${
      fontWeight || "normal"
    } ${MEASUREMENT_FONT_SIZE}px ${fontFamily}`;
    const cached = cache.get(shorthand);
    if (cached) return cached;
    const measured = measureFontMetrics(context, shorthand);
    cache.set(shorthand, measured);
    return measured;
  };
}

function measurementContext(): CanvasRenderingContext2D | null {
  if (typeof document === "undefined") return null;
  try {
    return document.createElement("canvas").getContext("2d");
  } catch {
    // jsdom throws rather than returning null; the fallback metrics stand in.
    return null;
  }
}

function measureFontMetrics(
  context: CanvasRenderingContext2D,
  shorthand: string,
): PsdFontMetrics {
  context.font = SENTINEL_FONT;
  context.font = shorthand;
  if (context.font === SENTINEL_FONT) return FALLBACK_FONT_METRICS;

  const metrics = context.measureText(MEASUREMENT_SAMPLE);
  const ascent = metrics.fontBoundingBoxAscent;
  const descent = metrics.fontBoundingBoxDescent;
  // Undefined without font-box metrics; zero ascent means nothing usable resolved.
  if (!Number.isFinite(ascent) || !Number.isFinite(descent)) {
    return FALLBACK_FONT_METRICS;
  }
  if (ascent <= 0 || descent < 0) return FALLBACK_FONT_METRICS;
  return {
    ascent: ascent / MEASUREMENT_FONT_SIZE,
    descent: descent / MEASUREMENT_FONT_SIZE,
  };
}
